import React, { useState, useEffect, useRef } from 'react';
import './Timeline.css';

const Timeline = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeYear, setActiveYear] = useState(0);
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);

  const milestones = [
    {
      year: '1873',
      title: 'Foundation in Geneva',
      description: 'Heinrich Müller establishes CHRONOS in the heart of Geneva\'s watchmaking district. The first workshop employs 8 master craftsmen.',
      image: '/images/heritage/vintage-1920s.png',
      achievement: 'First Workshop Established',
      color: '#C0A030'
    },
    {
      year: '1924',
      title: 'The First Tourbillon',
      description: 'CHRONOS unveils its first tourbillon movement, revolutionizing accuracy in mechanical timekeeping. Only 25 pieces produced.',
      image: '/images/heritage/vintage-1920s.png',
      achievement: 'Patent No. 14,728',
      color: '#D4AF37'
    },
    {
      year: '1965',
      title: 'Atomic Precision Standard',
      description: 'Introduction of the revolutionary atomic timekeeping standard. CHRONOS timepieces achieve unprecedented accuracy of ±1 second per year.',
      image: '/images/heritage/vintage-1960s.png',
      achievement: 'Chronometer Certification',
      color: '#F4E5A0'
    },
    {
      year: '2025',
      title: 'Modern Innovation',
      description: 'While honoring 152 years of tradition, CHRONOS integrates cutting-edge materials and precision engineering with timeless Swiss craftsmanship.',
      image: '/images/heritage/modern-2024.png',
      achievement: 'Legacy Continues',
      color: '#D4AF37'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Handle scroll to determine active year
  useEffect(() => {
    const handleScroll = () => {
      if (timelineRef.current) {
        const timeline = timelineRef.current;
        const scrollLeft = timeline.scrollLeft;
        const itemWidth = timeline.scrollWidth / milestones.length;
        const index = Math.round(scrollLeft / itemWidth);
        setActiveYear(Math.min(index, milestones.length - 1));
      }
    };

    const timeline = timelineRef.current;
    if (timeline) {
      timeline.addEventListener('scroll', handleScroll);
      return () => timeline.removeEventListener('scroll', handleScroll);
    }
  }, [milestones.length]);

  const scrollToYear = (index) => {
    if (timelineRef.current) {
      const timeline = timelineRef.current;
      const itemWidth = timeline.scrollWidth / milestones.length;
      timeline.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="heritage" 
      ref={sectionRef}
      className={`timeline-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="timeline-container">
        {/* Section Header */}
        <div className="timeline-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">Our Heritage</span>
            <span className="label-line"></span>
          </div>
          <h2 className="timeline-title">152 Years of Excellence</h2>
          <p className="timeline-subtitle">
            From a small Geneva workshop to a globally renowned manufacture, 
            CHRONOS has remained dedicated to the art of fine watchmaking.
          </p>
        </div>

        {/* Year Navigation */}
        <div className="year-navigation">
          {milestones.map((milestone, index) => (
            <button
              key={milestone.year}
              className={`year-btn ${activeYear === index ? 'active' : ''}`}
              onClick={() => scrollToYear(index)}
            >
              <span className="year-number">{milestone.year}</span>
              <span className="year-indicator"></span>
            </button>
          ))}
          <div className="year-progress-bar">
            <div 
              className="year-progress-fill"
              style={{
                width: `${((activeYear + 1) / milestones.length) * 100}%`
              }}
            ></div>
          </div>
        </div>

        {/* Horizontal Timeline Scroll */}
        <div className="timeline-scroll-container" ref={timelineRef}>
          <div className="timeline-track">
            {milestones.map((milestone, index) => (
              <div key={milestone.year} className="timeline-item">
                {/* Background Image */}
                <div className="timeline-image-container">
                  <img 
                    src={milestone.image} 
                    alt={`CHRONOS in ${milestone.year}`}
                    className="timeline-image"
                  />
                  <div className="timeline-image-overlay"></div>
                </div>

                {/* Content */}
                <div className="timeline-content">
                  <div className="timeline-year-badge">
                    <span className="badge-year">{milestone.year}</span>
                  </div>

                  <div className="timeline-text">
                    <h3 className="timeline-milestone-title">{milestone.title}</h3>
                    <p className="timeline-description">{milestone.description}</p>
                    
                    <div className="timeline-achievement">
                      <span className="achievement-icon">◆</span>
                      <span className="achievement-text">{milestone.achievement}</span>
                    </div>
                  </div>

                  {/* Decorative Element */}
                  <div 
                    className="timeline-accent"
                    style={{ background: milestone.color }}
                  ></div>
                </div>

                {/* Era Label */}
                <div className="timeline-era">
                  {index === 0 && 'Foundation Era'}
                  {index === 1 && 'Innovation Era'}
                  {index === 2 && 'Precision Era'}
                  {index === 3 && 'Modern Era'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Instruction */}
        <div className="scroll-instruction">
          <span className="scroll-icon">←</span>
          <span className="scroll-text">Scroll to travel through time</span>
          <span className="scroll-icon">→</span>
        </div>

        {/* Bottom Stats */}
        <div className="heritage-stats">
          <div className="heritage-stat">
            <span className="stat-number">152</span>
            <span className="stat-label">Years of Excellence</span>
          </div>
          <div className="stat-divider"></div>
          <div className="heritage-stat">
            <span className="stat-number">50,000+</span>
            <span className="stat-label">Timepieces Crafted</span>
          </div>
          <div className="stat-divider"></div>
          <div className="heritage-stat">
            <span className="stat-number">4</span>
            <span className="stat-label">Generations</span>
          </div>
          <div className="stat-divider"></div>
          <div className="heritage-stat">
            <span className="stat-number">1</span>
            <span className="stat-label">Commitment to Perfection</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Timeline;